/** @module csi/slicer/slicerMetricsDlg */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/json",
	"dojo/keys",
	"dijit/_WidgetBase",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/registry",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/widget/FilteringSelect",
	"dojo/text!csi/slicer/slicerMetricsDlg.html",
	// Used in HTML template
	"dijit/form/DropDownButton",
	"dijit/form/RadioButton",
	"csi/widget/Select",
	"csi/widget/CheckedMultiSelectDropdown",
	"csi/widget/InvalidMessage",
	"csi/slicer/slicerMetricFormat"
], function(declare,domClass,json,keys,_WidgetBase,_TemplatedMixin,_WidgetsInTemplateMixin,registry,_,html,locale,FilteringSelect,template) {

	/**
	 * This module is a widget, that being used as content of popup dialog: slicerEditor.js -> _onActionAddCustomMetric()
	 * Widget has two always visible main fields:
	 *  - Metric Name
	 *  - Calculation type
	 * Widget changes its appearance depend on Calculation Type value: upon this value change, different set of other fields become visible,
	 * allowing configuring selected calculation type.
	 *
	 * Widget has template (slicerMetricsDlg.html). Template has number of controls. Some controls are always visible,
	 * another controls can be shown/hidden to achieve required set of fields.
	 *
	 * Variable "nodes" contains full set of widget's controls node references (references to divs that wrap actual input controls).
	 * Each node in template also has additional named sub node, which referring to the input control itself (input node name = wrapping nodeName + "Control")
	 * Function _getControl() returns input control's node if you supply wrapping div node name.
	 *
	 * Variable "subViews" defines set of required fields for each calculation type.
	 * See function _showSubView() to understand how widget manipulating nodes, showing/hiding nodes depend on selected calculation type.
	 */

	/** Sub Views constants */
	var sum = 'sum';
	var dif = 'dif';
	var prd = 'prd';
	var avg = 'avg';
	var mix = 'mix';
	var mrg = 'mrg';
	var mrk = 'mrk';
	var vrn = 'vrn';

	var FIELD_VALUE = "value";

	/** Sub View Nodes - set of nodes, that can be used in subview. Each node is one control with label9s) (multi select, select, radio buttons) */
	var nodes = {
		calcFor: "calcForNode",
		metricA: "metricANode",
		metricB: "metricBNode",
		showResValue: "showResValueNode",
		showResPercent: "showResPercentNode",
		calcForSingle: "calcForSingleNode", // for now this uses static only store
		typePercentOf: "typePercentOfNode",
		costMetric: "costMetricNode",
		revenueMetric: "revenueMetricNode"
	};
	/** Old view nodes, left for compatibility */
	var oldNodes = {
		typeSubTotal: "typeSubTotalNode"
	};

	/* Sub view specification - define which controls are included in each sub view. Each node can only be used once in given sub view and no reordering */
	var subViews = {
			sum:[nodes.calcFor], // Summ
			dif:[nodes.metricA, nodes.metricB, nodes.showResValue, nodes.showResPercent], // Difference
			prd:[nodes.calcFor], // Product
			avg:[nodes.calcFor], // Average
			mix:[nodes.calcForSingle, nodes.typePercentOf], // % Mix
			mrg:[nodes.costMetric, nodes.revenueMetric], // % Margin
			mrk:[nodes.costMetric, nodes.revenueMetric], // % Markup
			vrn:[nodes.metricA, nodes.metricB] // % Variance
	};

	/* Sub view tooltip - define what will be shown on math display if sub view is not yet configured */
	var subViewTooltip = {
			sum:"= MetricA + MetricB + ...", // Summ
			dif:"= MetricA - MetricB", // Difference
			prd:"= MetricA * MetricB * ...", // Product
			avg:"= MetricA, MetricB ... / Number of selected metrics", // Average
			mix:"= (Metric / Sum(Metric))", // % Mix
			mrg:"= (Revenue metric - Cost metric) / Revenue metric", // % Margin
			mrk:"= (Selling Price - Cost) / Cost", // % Markup
			vrn:"= Var(MetricA, MetricB)" // % Variance
	};

	/** Options for color select */
	return declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin],{
		templateString: template,
		parseOnLoad: true,

		/**
		 * @typedef {Object} MetricObject
		 * 	@property {string} id - metric Key
		 * 	@property {string} name - metric name
		 * 	@property {string} func - metric expression (for custom metrics this field is formula representation
		 * 	@property {boolean}	count - if function is a count
		 * 	@property {string} total - metric key to calcualte total or ""
		 * 	@property {string} uniq - metric key to calculate unique
		 * 	@property {boolean} dynamic - dynamic metric (includes customs)
		 * 	@property {boolean} custom - custom metric
		 * 	@property {customPresentation|string} presentation - custom metric presentation object
		 * 	@property {string} format - different from slicer/_base metric structure, those have heading field instead and heading includes format
		 * 	@property {ParamsObject} params - parameters for Change/CreateCustomDynamicMetric post request
		 *
		 * @typedef {Object} customPresentation
		 * 	@property {string} name - custom metric name
		 * 	@property {string} subview - subview aka custom operation {'sum','dif','prd'...}
		 * 	@property {string[]} [calcForNode] - array of metric keys
		 * 	@property {string} [metricANode] - metric key
		 * 	@property {string} [metricBNode] - metric key
		 * 	@property {string} [calcForSingleNode] - metric key
		 * 	@property {string} [costMetricNode] - metric key
		 * 	@property {string} [revenueMetricNode] - metric key
		 * 	@property {string} [showResValueNode]: show results radio button value
		 * 	@property {string} [typePercentOf]: show select for % mix aggregation type
		 * 	@property {string} [total]: custom metric aggregation type
		 *
		 * @typedef {ParamsObject} - data structure for Change/CreateCustomDynamicMetric post request
		 * 	@property {string} MetricName
		 * 	@property {string} AggregateFunction
		 * 	@property {json string} Presentation:
		 *		{
		 *			subview: {string} - calculation type
		 * 			name {string}, - metric name
		 * 			showResValue: {string}, - optional, raido button value
		 * 			typePercentOf: {string}, - optional, select value
		 * 			calcFor: {string[]} - optional, check box multi-select values, set of metrics
		 * 			metricA, metricB, calcForSingle, costMetric, revenueMetric: {string} - optional
		 * 			total: {string} - aggregation type
		 * 		}
		 * 	@property {string} ValueType - always 'double',
		 * 	@property {string} Format
		 *
		 */
		value: null,

		/** @property {Memory} fullStore - all metrics */
		fullStore: null,
		/** @property {Memory} metricStore - Non-custom metrics store (custom and date metrics excluded)*/
		metricStore: null,
		/** @property {string} subView - name of currently selected sub view (related to selected calculation type) */
		subView: null,

		startup: function(){
			this.inherited(arguments);

			this.subView = this.calcTypeNode.get(FIELD_VALUE);
			this._showSubView();
		},

		_setValueAttr: function(newValue) {
			this._set("value", newValue);
			this.formatDropdownNodeControl.parentButton = this.formatNodeControl;
			if (newValue){ // edit existing action?
				var presentation = newValue.presentation;
				// set format
				var h = newValue.heading;
				if (h){
					format = h.m_format;
				} else {
					format = newValue.format;
				}
				this.formatNodeControl.set(FIELD_VALUE, format);
				this.formatDropdownNodeControl.formatPresentation = presentation.formatPresentation;
				this.totalNodeControl.set(FIELD_VALUE, presentation.total);

				//set presentation
				for (var field in presentation) {
					var val = presentation[field];
					switch (field){
						case "name":
							this.metricNameNode.set(FIELD_VALUE, val);
							break;
						case "subview":
							this.calcTypeNode.set(FIELD_VALUE, val);
							break;
						case nodes.calcFor:
							this._getControl(field).resetSelected(val);
							break;
						case nodes.metricA:
						case nodes.metricB:
						case nodes.calcForSingle:
						case nodes.costMetric:
						case nodes.revenueMetric:
						case nodes.typePercentOf:
							this._getControl(field).set(FIELD_VALUE, val);
							break;
						case nodes.showResValue:
							if (val=="val" ){
								this.showResPercentNodeControl.set("checked", false);
								this.showResValueNodeControl.set("checked", true);
							} else {
								this.showResPercentNodeControl.set("checked", true);
								this.showResValueNodeControl.set("checked", false);
							}
							break;
						case oldNodes.typeSubTotal: // Pick up old settings (subtotal/grandtotal) and convert to PercentOF
							if (val=="sub" ){
								this.typePercentOfNodeControl.set(FIELD_VALUE,"colsub");
							} else {
								this.typePercentOfNodeControl.set(FIELD_VALUE,"colgrand");
							}
							break;
					}
				}
			}
		},

		/**
		 * Return control's input node for given node
		 * @param {string} node - name of the node, that contains the control
		 * @return {Object} - control's object or undefined
		 */
		_getControl: function(node){
			return this[node+'Control'];
		},

		/**
		 * On Calculation Type change event
		 * @param {value} string
		 */
		_onCalcTypeChange: function(value){
			this.subView = value;
			this.invalidNode.set('value', '');
			this._showSubView();
		},

		/**
		 * Show required sub nodes, hide not used ones
		 */
		_showSubView: function(){
			var visibleNodes = subViews[this.subView];
			_.each(nodes, function(node){
				var bVisible = visibleNodes.indexOf(node) >= 0;
				html.setVisible(this[node], bVisible);
			}, this);
			html.setVisible(this.totalNode, this.subView != mix); // % mix ignores dynamic aggregation
			this._displayMath();
			this._setDefaultFormat();
		},

		/**
		 * Set format based on selected calculation type.
		 */
		_setDefaultFormat: function(){
			/* TODO */
		},

		/**
		 * Convert metric key to metric name
		 * @param {string} key - metric key
		 * @return {string} - metric name
		 */
		_getMetricName: function(key){
			var metric = this.metricStore.get(key);
			if (metric) {
				return metric.name || "";
			} else {
				return "";
			}
		},

		/**
		 * Convert metric keys to metric names
		 * @param {string[]} keys  - array of metric keys
		 * @return {string[]} names - array of metric names
		 */
		_getMetricNames: function(keys){
			return _.map(keys, this._getMetricName, this);
		},

		/**
		 * Return tooltip string for currently selected Calculation Type (this.subView)
		 * @return {string}
		 */
		_getSubViewTooltip: function(){
			var self = this;

			function getNames(control){
				return self._getMetricNames(control.get(FIELD_VALUE));
			}
			function getName(control){
				return self._getMetricName(control.get(FIELD_VALUE));
			}

			var txt = "";
			switch (this.subView){
				case sum:
					txt = getNames(this.calcForNodeControl).join(" + ");
					break;
				case dif:
					var B = getName(this.metricBNodeControl);
					txt = getName(this.metricANodeControl) + ' - ' + B;
					bPercentage = this.showResPercentNodeControl.checked;
					if (bPercentage){
						txt = '(' + txt + ') / ' + B;
					}
					break;
				case prd:
					txt = getNames(this.calcForNodeControl).join(" * ");
					break;
				case avg:
					var val = this.calcForNodeControl.get(FIELD_VALUE);
					txt = this._getMetricNames(val).join(" + ") + ' / ' + val.length;
					break;
				case mrg:
					var rev = getName(this.revenueMetricNodeControl);
					txt = '(' + rev + ' - ' + getName(this.costMetricNodeControl) + ') / ' + rev;
					break;
				case mrk:
					var cost = getName(this.costMetricNodeControl);
					txt = '(' + getName(this.revenueMetricNodeControl) + ' - ' + cost + ') / ' + cost;
					break;
				case mix:
					var metricName = getName(this.calcForSingleNodeControl);
					txt = metricName + ' / Sum(' + metricName + ')';
					break;
				case vrn:
					txt = 'Var(' + getName(this.metricANodeControl) + ', ' + getName(this.metricBNodeControl) + ')';
					break;
			}
			return '= ' + txt;
		},

		/**
		 * Display calculation tooltip: either default tooltip if configuration is incomplete
		 * or real tooltip if validation was successful
		 */
		_displayMath: function(){
			if (this._validateNodes(true)){
				txt = this._getSubViewTooltip();
			} else {
				txt = subViewTooltip[this.subView];
			}
			html.setInnerText(this.displayMathNode, txt);
		},

		/**
		 * Validate data (_validate()) and get new values (updateValue()) if data is valid
		 * This is high level validation function.
		 * @return {boolean} - true if valid
		 */
		validate: function(){
			var res = this._validate();
			if (res){
				this.updateValue();
			}
			return res;
		},

		/**
		 * Validate configuration, including always visible fields (Name and Type)
		 * and call variable nodes validation function to validate other nodes.
		 * This is medium level validation function.
		 * @return {boolean} - true if valid
		 */
		_validate: function(){
			var res = true;
			this.invalidNode.set('value', '');

			res = this.metricNameNode.isValid();
			if (!res){
				this.invalidNode.set('value', locale.resource("RequiredValue"));
				this.metricNameNode.focus();
			} else {
				var name = this.metricNameNode.get(FIELD_VALUE);
				var metric = this.value; // edit mode? metric to edit : null;
				if ( ((metric && (metric.name != name)) || !metric) && (this.fullMetricStore.query({name:name}).length > 0)){
					this.invalidNode.set('value', locale.resource("SlicerUniqueNameErr"));
					this.metricNameNode.focus();
					res = false;
				} else {
					res = this._validateNodes();
				}
			}
			return res;
		},

		/**
		 * Validate data specific to selected math type (nodes). Travers nodes, call each node validation ( _validateNode() )
		 * This is lover level validation function.
		 * @param {boolean) bQuiet - display dialog error message and sed node focus if not "true"
		 * @return {string} - return error message or "" if validation success
		 */
		_validateNodes: function(bQuiet){
			var err = "";
			var visibleNodes = subViews[this.subView];
			return !_.some(visibleNodes, function(node){
				err = this._validateNode(node);
				if (err){
					if (!bQuiet){
						this.invalidNode.set('value', err);
						this._getControl(node).focus();
					}
					return true;
				}
			}, this);
		},

		/**
		 * Validate single node. This is low level node validation function.
		 * @param {string} node - name of the node to analyze (see local variable "nodes")
		 * @return {string} - return empty string is valid, otherwise return localized error message
		 */
		_validateNode: function(node){
			// Check if required value is selected and not empty
			switch (node) {
				case nodes.metricA:
				case nodes.metricB:
				case nodes.costMetric:
				case nodes.revenueMetric:
				case nodes.calcForSingle:
					if (!this._getControl(node).get(FIELD_VALUE)){
						return locale.resource("SlicerSelectMetric");
					}
			}

			switch (node) {
				case nodes.calcFor:
					return (this._getControl(node).get(FIELD_VALUE).length > 1) ? "" : locale.resource("SlicerSelectTwo");
				case nodes.metricB:
					if (this._getControl(node).get(FIELD_VALUE) != this._getControl(nodes.metricA).get(FIELD_VALUE)){
						return "";
					}
					break;
				case nodes.revenueMetric:
					if (this._getControl(node).get(FIELD_VALUE) != this._getControl(nodes.costMetric).get(FIELD_VALUE)){
						return "";
					}
					break;
				default:
					return "";
			}
			return locale.resource("SlicerSelectTwoDifferent");
		},

		/**
		 * Create unique metric key from metric name. Name might have any set of characters. Name may be not unique.
		 * Key might have alpha-numeric and underscore and must be unque acroll all slicer metrics (including static, dynamic and custom metrics).
		 * @param {string} name
		 * @return {string} key
		 */
		_createKey: function(name){
			var key = name.replace(/\W/g,"");
			// adding time will make metric key unique even if user delete this metric and create another with the same name.
			var theKey = key.substring(0,8) + "_" + new Date().getTime();
			var id = 0;
			// Find available unique metric key
			while (this.fullMetricStore.get(theKey)){
				theKey = key + (++id);
			}
			return theKey;
		},

		/**
		 * Serialize name, type and variable nodes values.
		 * @param {string} metricName
		 * @return {string} - JSON string
		 */
		_getPresentation: function(metricName){
			var obj = {
					subview:this.subView,
					name: metricName,
					total: this.totalNodeControl.get(FIELD_VALUE),
					formatPresentation: this.formatDropdownNodeControl.getPresentation()
				};

			var visibleNodes = subViews[this.subView];
			_.each(visibleNodes, function(node){
				switch (node) {
					case nodes.calcFor:
					case nodes.metricA:
					case nodes.metricB:
					case nodes.calcForSingle:
					case nodes.costMetric:
					case nodes.revenueMetric:
					case nodes.typePercentOf:
						obj[node] = this._getControl(node).get(FIELD_VALUE);
						break;
					case nodes.showResValue:
						obj[node] = $('input[type=radio][name=radioShowRes]:checked')[0].value;
						break;
				}
			}, this);
			return obj;
		},

		/**
		 * Retrieve data from controls, set values for output metricParams object
		 */
		updateValue: function(){
			var m = {};
			var metricName = this.metricNameNode.get(FIELD_VALUE);
			var format = this.formatNodeControl.get(FIELD_VALUE);
			var metricKey = this.value ? this.value.id : this._createKey(metricName);
			m.presentation = this._getPresentation(metricName);
			m.params = { // Params for server query request
				MetricName: metricKey,
				AggregateFunction: this._getSubViewTooltip(),
				Presentation: json.stringify(m.presentation),
				ValueType: 'double',
				Format: (format == "") ? " " : format // adding " " because otherwise server will not update format with post request
			};
			// Define new metric for temporary use with Manage Metrics dialog(then the entire view will be refreshed)
			m.id = metricKey,
			m.name = m.presentation.name;
			m.func = m.params.AggregateFunction;
			m.count= false;
			m.total= null;
			m.uniq = null;
			m.dynamic = true;
			m.custom = true;
			m.format = format;
			this._set("value", m);
		}

	});
});
